import { computed, onScopeDispose, ref, watch } from 'vue'
import { defineStore } from 'pinia'

export type Theme = 'dark' | 'light'
export type ThemePreference = Theme | 'system'

// 获取系统默认主题
const getSystemTheme = (): Theme => {
  if (typeof window === 'undefined') return 'dark'
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  return prefersDark ? 'dark' : 'light'
}

function resolveThemeColor(theme: Theme) {
  return theme === 'dark' ? '#141414' : '#ffffff'
}

function syncBrowserTheme(theme: Theme) {
  if (typeof document === 'undefined') {
    return
  }

  const themeColor = resolveThemeColor(theme)
  const themeColorMeta = document.querySelector('meta[name="theme-color"]')
  const appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')

  document.documentElement.style.colorScheme = theme
  document.documentElement.style.backgroundColor = themeColor
  document.body.style.backgroundColor = themeColor

  if (themeColorMeta) {
    themeColorMeta.setAttribute('content', themeColor)
  }

  if (appleStatusBarMeta) {
    appleStatusBarMeta.setAttribute('content', theme === 'dark' ? 'black-translucent' : 'default')
  }
}

export const useThemeStore = defineStore(
  'theme',
  () => {
    const systemTheme = ref<Theme>(getSystemTheme())
    const themePreference = ref<ThemePreference>('system')
    const currentTheme = computed<Theme>(() =>
      themePreference.value === 'system' ? systemTheme.value : themePreference.value,
    )

    const applyTheme = (theme: Theme) => {
      if (typeof document !== 'undefined') {
        if (theme === 'dark') {
          document.documentElement.classList.add('dark')
        } else {
          document.documentElement.classList.remove('dark')
        }
        document.documentElement.setAttribute('data-theme', theme)
        syncBrowserTheme(theme)
      }
    }

    if (typeof window !== 'undefined') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      const handleSystemThemeChange = (event?: MediaQueryListEvent) => {
        systemTheme.value = (event?.matches ?? mediaQuery.matches) ? 'dark' : 'light'
      }

      handleSystemThemeChange()
      mediaQuery.addEventListener('change', handleSystemThemeChange)
      onScopeDispose(() => {
        mediaQuery.removeEventListener('change', handleSystemThemeChange)
      })
    }

    const toggleTheme = () => {
      themePreference.value = currentTheme.value === 'dark' ? 'light' : 'dark'
    }

    const setTheme = (theme: ThemePreference) => {
      themePreference.value = theme
    }

    watch(
      currentTheme,
      (newTheme) => {
        applyTheme(newTheme)
      },
      { immediate: true },
    )

    return {
      currentTheme,
      systemTheme,
      themePreference,
      toggleTheme,
      setTheme,
    }
  },
  {
    persist: {
      key: 'theme',
      storage: localStorage,
      paths: ['themePreference'],
    },
  },
)
